import { useState, useEffect, useMemo } from 'react';
import AdminLayout from '@/components/Layout/AdminLayout';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog.tsx';
import { showToast } from '@components/Layout/Toast';
import TradeFilterModal from './components/TradeFilterModal';
import FilterBacktestsFilters from './components/FilterBacktestsFilters';
import FilterBacktestsResults from './components/FilterBacktestsResults';
import type { TradeFilter, EntityTypeUnion, Entity } from '@einhaus/project314-types';
import type { TradingStrategyBacktest } from '@/types/appRouter';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingRequisites } from '@/hooks/useTradingRequisites';
import { getDefaultColumns, createColumnDefinitions } from './backtestColumns';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useLocation } from 'react-router-dom';

const FilterBacktests = () => {
    const [selectedStrategyId, setSelectedStrategyId] = useState<string>('');
    const [selectedEntityType, setSelectedEntityType] = useState<string>('');

    const [selectedEntity, setSelectedEntity] = useState<{
        entityId: number;
        entityType: EntityTypeUnion['entityType'];
        symbol?: string;
        name?: string;
    } | null>(null);

    const [beforeDate, setBeforeDate] = useState<string>('');
    const [tradeFilters, setTradeFilters] = useState<TradeFilter[]>([]);
    const [entities, setEntities] = useState<Entity[]>([]);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [selectedBacktestIds, setSelectedBacktestIds] = useState<Set<number>>(new Set());

    const [isDeleteSelectedOpen, setIsDeleteSelectedOpen] = useState(false);
    const [isFilterDeleteOpen, setIsFilterDeleteOpen] = useState(false);

    const [filterDeletePreview, setFilterDeletePreview] = useState<{
        toDeleteCount: number;
        sampleIds: number[];
    } | null>(null);

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
        // Load from localStorage or use defaults
        const saved = localStorage.getItem('filterBacktests.columns');

        if (saved) {
            try {
                return new Set(JSON.parse(saved));
            } catch {
                // If parsing fails, use defaults
                return getDefaultColumns();
            }
        }

        return getDefaultColumns();
    });

    // Save column preferences to localStorage
    const handleVisibilityChange = (columns: Set<string>) => {
        setVisibleColumns(columns);
        localStorage.setItem('filterBacktests.columns', JSON.stringify([...columns]));
    };

    // Fetch trading requisites with tRPC
    const { isLoading: isLoadingRequisites, requisites } = useTradingRequisites({
        errorConfig: {
            fallbackErrorMessage: 'Failed to fetch trading requisites',
            showErrorToast: true
        }
    });

    // Extract data from requisites
    const tradingStrategies = useMemo(() => requisites?.tradingStrategies || [], [requisites?.tradingStrategies]);

    // Determine if initial data is loading
    const isLoading = isLoadingRequisites;

    const handleAddFilter = (filter: TradeFilter) => {
        setTradeFilters([...tradeFilters, filter]);
    };

    const handleRemoveFilter = (id: string) => {
        setTradeFilters(tradeFilters.filter((filter) => filter.id !== id));
    };

    // Setup search parameters state
    const [searchParams, setSearchParams] = useState<{
        strategyId: number | null;
        entityType: EntityTypeUnion['entityType'] | null;
        derivativeType: 'Option' | 'Future' | null;
        entityId: number | null;
        entitySearchType: EntityTypeUnion['entityType'] | null;
        beforeDate: string | null;
        tradeFilters: TradeFilter[];
        optionBuyingStrategyIds: number[];
        exitCandidateIds: number[];
    } | null>(null);

    const location = useLocation();
    const [didInitFromLocation, setDidInitFromLocation] = useState(false);

    // Initialize filters from navigation state (if present)
    useEffect(() => {
        if (didInitFromLocation) return;

        const state = location.state as {
            strategyId?: number;
            entityType?: EntityTypeUnion['entityType'] | null;
            optionBuyingStrategyIds?: number[];
            exitCandidateIds?: number[];
            autoSearch?: boolean;
        } | null;

        if (state?.strategyId) {
            setSelectedStrategyId(state.strategyId.toString());

            if (state.entityType) {
                setSelectedEntityType(state.entityType);
            }

            const shouldAutoSearch = state.autoSearch ?? true;

            if (shouldAutoSearch) {
                setSearchParams({
                    strategyId: state.strategyId,
                    entityType: state.entityType ?? null,
                    derivativeType: null,
                    entityId: null,
                    entitySearchType: null,
                    beforeDate: null,
                    tradeFilters: [],
                    optionBuyingStrategyIds: state.optionBuyingStrategyIds ?? [],
                    exitCandidateIds: state.exitCandidateIds ?? []
                });
            }
        }

        setDidInitFromLocation(true);
    }, [didInitFromLocation, location.state, setSelectedStrategyId, setSelectedEntityType]);

    // Fetch backtests with tRPC using the proper pattern
    const backtestsQuery = useAppQuery(
        {
            ...trpc.backtest.fetchBacktests.queryOptions(
                searchParams || {
                    strategyId: null,
                    entityType: null,
                    derivativeType: null,
                    entityId: null,
                    entitySearchType: null,
                    beforeDate: null,
                    tradeFilters: []
                }
            ),
            enabled: searchParams !== null
        },
        {
            fallbackErrorMessage: 'Failed to fetch backtests',
            showErrorToast: true
        }
    );

    // Handle backtests query results
    useEffect(() => {
        if (backtestsQuery.data) {
            setEntities(backtestsQuery.data.entities);
            // Reset selection when results change
            setSelectedBacktestIds(new Set());

            if (searchParams !== null) {
                showToast('Backtests loaded successfully', 'success');
            }
        }
    }, [backtestsQuery.data, searchParams]);

    const backtests: TradingStrategyBacktest[] = backtestsQuery.data?.backtests ?? [];

    // Analyze best parameter combinations for current strategy/entity filter
    const parameterAnalysisQuery = useAppQuery(
        {
            ...trpc.backtest.analyzeParameterCombinations.queryOptions(
                searchParams?.strategyId
                    ? {
                          strategyId: searchParams.strategyId,
                          entityType: searchParams.entityType,
                          entityId: searchParams.entityId || null,
                          keyAssetsOnly: false,
                          strategyVersionKeyMode: 'current'
                      }
                    : {
                          strategyId: 0,
                          entityType: null,
                          entityId: null,
                          keyAssetsOnly: false,
                          strategyVersionKeyMode: 'current'
                      }
            ),
            enabled: !!searchParams?.strategyId
        },
        {
            fallbackErrorMessage: 'Failed to analyze backtest parameter combinations',
            showErrorToast: true
        }
    );

    const analysisCombinations = parameterAnalysisQuery.data?.combinations ?? [];
    const analysisSummary = parameterAnalysisQuery.data?.summary ?? null;
    const analysisByExitCandidate = parameterAnalysisQuery.data?.byExitCandidate ?? [];
    const analysisByOptionStrategy = parameterAnalysisQuery.data?.byOptionStrategy ?? [];

    // Handle search button click
    const handleSearch = () => {
        if (!selectedStrategyId) {
            showToast('Please select a strategy', 'error');
            return;
        }

        if (!selectedEntityType) {
            showToast('Please select an entity type', 'error');
            return;
        }

        const entityType = selectedEntityType === 'AssetOption' ? 'Asset' : (selectedEntityType as EntityTypeUnion['entityType']);
        const derivativeType = selectedEntityType === 'AssetOption' ? 'Option' : null;

        setSearchParams({
            strategyId: +selectedStrategyId,
            entityType,
            derivativeType,
            entityId: selectedEntity?.entityId || null,
            entitySearchType: selectedEntity?.entityType || null,
            beforeDate: beforeDate || null,
            tradeFilters,
            optionBuyingStrategyIds: [],
            exitCandidateIds: []
        });
    };

    const entityTypeOptions = [
        { value: '', label: 'Choose an entity type', disabled: true },
        { value: 'Asset', label: 'Asset' },
        { value: 'Cryptocurrency', label: 'Cryptocurrency' },
        { value: 'AssetIndex', label: 'Asset Index' },
        { value: 'AssetOption', label: 'Asset Option' }
    ];

    // Create column definitions based on visible columns
    const columns: ColumnDef<TradingStrategyBacktest>[] = useMemo(() => {
        const hasTradeDetails = Boolean(searchParams?.beforeDate) || (searchParams?.tradeFilters?.length ?? 0) > 0;

        return createColumnDefinitions({
            visibleColumns,
            entities,
            tradingStrategies,
            optionBuyingStrategies: backtestsQuery.data?.optionBuyingStrategies || [],
            hasTradeDetails
        });
    }, [visibleColumns, entities, tradingStrategies, backtestsQuery.data, searchParams]);

    const allSelected = backtests.length > 0 && selectedBacktestIds.size === backtests.length;

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedBacktestIds(new Set());
        } else {
            setSelectedBacktestIds(new Set(backtests.map((b) => b.id)));
        }
    };

    const toggleSelectBacktest = (id: number) => {
        setSelectedBacktestIds((prev) => {
            const next = new Set(prev);

            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
    };

    const columnsWithSelection: ColumnDef<TradingStrategyBacktest>[] = useMemo(() => {
        const selectionColumn: ColumnDef<TradingStrategyBacktest> = {
            id: 'select',
            header: () => <input checked={allSelected} className="h-4 w-4 cursor-pointer" type="checkbox" onChange={toggleSelectAll} />,
            cell: (info) => {
                const backtest = info.row.original;
                const isSelected = selectedBacktestIds.has(backtest.id);
                return (
                    <input
                        checked={isSelected}
                        className="h-4 w-4 cursor-pointer"
                        type="checkbox"
                        onChange={() => toggleSelectBacktest(backtest.id)}
                        onClick={(event) => event.stopPropagation()}
                    />
                );
            },
            size: 40,
            meta: {
                headerClassName: 'w-10 text-center',
                className: 'w-10 text-center',
                sticky: 'left',
                stickyOffset: 0
            }
        };

        return [selectionColumn, ...columns];
    }, [columns, allSelected, selectedBacktestIds]);

    const deleteBacktestsByIdsMutation = useTrpcMutation(
        trpc.backtest.deleteBacktestsByIds.mutationOptions({
            onSuccess: () => {
                void backtestsQuery.refetch();
            }
        }),
        {
            successMessage: 'Backtests deleted successfully',
            fallbackErrorMessage: 'Failed to delete backtests'
        }
    );

    const deleteBacktestsByFilterMutation = useTrpcMutation(
        trpc.backtest.deleteBacktestsByFilter.mutationOptions({
            onSuccess: () => {
                void backtestsQuery.refetch();
            }
        }),
        {
            fallbackErrorMessage: 'Failed to delete backtests'
        }
    );

    const handleDeleteSelectedClick = () => {
        if (!selectedBacktestIds.size) {
            showToast('No backtests selected', 'info');
            return;
        }

        setIsDeleteSelectedOpen(true);
    };

    const handleConfirmDeleteSelected = async () => {
        try {
            const ids = Array.from(selectedBacktestIds);

            if (!ids.length) {
                setIsDeleteSelectedOpen(false);
                return;
            }

            await deleteBacktestsByIdsMutation.mutateAsync({ ids });
            setSelectedBacktestIds(new Set());
        } catch {
            // Toasts handled by useTrpcMutation
        } finally {
            setIsDeleteSelectedOpen(false);
        }
    };

    const buildFilterDeleteParams = (dryRun: boolean) => {
        if (!searchParams?.strategyId) {
            throw new Error('No active search parameters for filter-based delete.');
        }

        return {
            strategyId: searchParams.strategyId,
            entityType: searchParams.entityType,
            entityId: searchParams.entityId,
            exitCandidateIds: searchParams.exitCandidateIds,
            optionBuyingStrategyIds: searchParams.optionBuyingStrategyIds,
            minAvgTradeProfit: null as number | null,
            olderThanDate: null as string | null,
            includePortfolioBacktests: false,
            dryRun
        };
    };

    const handleFilterDeletePreview = async () => {
        if (!searchParams?.strategyId) {
            showToast('Run a search before deleting by filter.', 'error');
            return;
        }

        try {
            const params = buildFilterDeleteParams(true);
            const preview = await deleteBacktestsByFilterMutation.mutateAsync(params);

            if (preview.success) {
                setFilterDeletePreview({
                    toDeleteCount: preview.toDeleteCount ?? 0,
                    sampleIds: preview.sampleIds ?? []
                });

                setIsFilterDeleteOpen(true);
            }
        } catch {
            // Toasts handled by useTrpcMutation
        }
    };

    const handleConfirmFilterDelete = async () => {
        if (!searchParams?.strategyId) {
            setIsFilterDeleteOpen(false);
            return;
        }

        try {
            const params = buildFilterDeleteParams(false);

            await deleteBacktestsByFilterMutation.mutateAsync(params);
            setFilterDeletePreview(null);
        } catch {
            // Toasts handled by useTrpcMutation
        } finally {
            setIsFilterDeleteOpen(false);
        }
    };

    const isSearchDisabled = !selectedStrategyId || !selectedEntityType || isLoading || backtestsQuery.isLoading;
    const isSearchLoading = backtestsQuery.isLoading && searchParams !== null;

    return (
        <AdminLayout containerSize="2200px" title="Filter Backtests">
            <div className="mb-4">
                <FilterBacktestsFilters
                    beforeDate={beforeDate}
                    entityTypeOptions={entityTypeOptions}
                    isSearchDisabled={isSearchDisabled}
                    isSearchLoading={isSearchLoading}
                    selectedEntity={selectedEntity}
                    selectedEntityType={selectedEntityType}
                    selectedStrategyId={selectedStrategyId}
                    tradeFilters={tradeFilters}
                    onChangeBeforeDate={setBeforeDate}
                    onChangeEntity={(value) => setSelectedEntity(value)}
                    onChangeEntityType={(value) => {
                        setSelectedEntityType(value);
                        setSelectedEntity(null);
                    }}
                    onChangeStrategyId={setSelectedStrategyId}
                    onOpenFilterModal={() => setIsFilterModalOpen(true)}
                    onRemoveFilter={handleRemoveFilter}
                    onSearch={handleSearch}
                />
            </div>

            <FilterBacktestsResults
                analysisByExitCandidate={analysisByExitCandidate}
                analysisByOptionStrategy={analysisByOptionStrategy}
                analysisCombinations={analysisCombinations}
                analysisSummary={analysisSummary}
                backtests={backtests}
                beforeDate={searchParams?.beforeDate ?? null}
                columnsWithSelection={columnsWithSelection}
                hasStrategyId={!!searchParams?.strategyId}
                isAnalysisLoading={parameterAnalysisQuery.isLoading}
                isBacktestsLoading={backtestsQuery.isLoading && searchParams !== null}
                isInitialLoading={isLoading}
                selectedBacktestIdsCount={selectedBacktestIds.size}
                selectedEntityType={selectedEntityType}
                selectedStrategyId={selectedStrategyId}
                visibleColumns={visibleColumns}
                onDeleteSelectedClick={handleDeleteSelectedClick}
                onFilterDeletePreview={handleFilterDeletePreview}
                onVisibilityChange={handleVisibilityChange}
            />

            <TradeFilterModal isOpen={isFilterModalOpen} onClose={() => setIsFilterModalOpen(false)} onSave={handleAddFilter} />

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText="Delete"
                confirmVariant="danger"
                isConfirmLoading={deleteBacktestsByIdsMutation.isPending}
                isOpen={isDeleteSelectedOpen}
                message={
                    <div className="space-y-2 text-sm">
                        <p>
                            Are you sure you want to delete <strong>{selectedBacktestIds.size}</strong> selected backtest
                            {selectedBacktestIds.size === 1 ? '' : 's'}?
                        </p>
                        <p>This will remove the backtests and all associated trades and transactions.</p>
                    </div>
                }
                title="Delete selected backtests"
                onCancel={() => setIsDeleteSelectedOpen(false)}
                onConfirm={handleConfirmDeleteSelected}
            />

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText="Delete all"
                confirmVariant="danger"
                isConfirmLoading={deleteBacktestsByFilterMutation.isPending}
                isOpen={isFilterDeleteOpen}
                message={
                    <div className="space-y-2 text-sm">
                        <p>
                            {filterDeletePreview ? (
                                <>
                                    This will delete <strong>{filterDeletePreview.toDeleteCount}</strong> backtest
                                    {filterDeletePreview.toDeleteCount === 1 ? '' : 's'} that match the current search criteria.
                                    {filterDeletePreview.sampleIds.length > 0 && (
                                        <>
                                            <br />
                                            <span>
                                                Example IDs:{' '}
                                                <span className="font-mono">{filterDeletePreview.sampleIds.slice(0, 10).join(', ')}</span>.
                                            </span>
                                        </>
                                    )}
                                </>
                            ) : (
                                'This will delete all backtests that match the current search criteria.'
                            )}
                        </p>
                        <p className="text-red-400">
                            This action cannot be undone. Make sure you have the correct strategy and filters selected.
                        </p>
                    </div>
                }
                title="Delete all backtests matching current search"
                onCancel={() => setIsFilterDeleteOpen(false)}
                onConfirm={handleConfirmFilterDelete}
            />
        </AdminLayout>
    );
};

export default FilterBacktests;
